import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Crash-safe wrapper. Catches any render-time exception in children and shows
 * a recoverable error panel instead of a black screen. Use sparingly — wrap
 * large surfaces like modals, pages, or risky 3rd-party components.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface in console for debugging — Sentry/Datadog wiring goes here later
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        className="border border-rose-500/40 bg-rose-500/[0.05] rounded-md p-6 max-w-2xl mx-auto m-8"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="font-mono uppercase tracking-widest text-rose-400 text-[10px] mb-1">
              Something went wrong
            </div>
            <p className="text-sm text-zinc-300">
              The form hit an unexpected error. Your other settings are safe — you can
              close this panel and try again.
            </p>
            <pre className="mt-3 text-[10px] text-zinc-500 whitespace-pre-wrap break-all max-h-24 overflow-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
            <div className="mt-4 flex gap-2">
              <button
                onClick={this.reset}
                data-testid="error-boundary-retry"
                className="text-xs font-mono uppercase tracking-widest px-3 py-2 border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 rounded-sm flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
              {this.props.onClose && (
                <button
                  onClick={this.props.onClose}
                  className="text-xs font-mono uppercase tracking-widest px-3 py-2 text-zinc-500 hover:text-zinc-200 rounded-sm"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
